import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader, Spinner } from '@/modules/common';
import { Routes } from '@/router/routes';

import { useMaskDetails } from '../api';
import { MaskForm } from '../components';
import { Mask } from '../types';

export const MaskDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();
  const {
    details,
    uploadUrlData,
    isFetching,
    refetchUploadUrl,
    updateStyleMutation,
  } = useMaskDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      largeUrl: details.imagesPublicUrls.large,
      smallUrl: details.imagesPublicUrls.small,
      transparentUrl: details.imagesPublicUrls.transparent,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
    };
  }, [details]);

  const updateMask = async (data) => {
    if (uploadUrlData?.maskId) {
      try {
        const body = deepDifference<Mask>(data, defaultValues);
        await updateStyleMutation.mutateAsync({
          ...body,
          id: params.id,
        });
        toast.success(`Mask “${data.name}” updated successfully!`);
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  if (isFetching) {
    return <Spinner />;
  }

  return (
    <MaskForm
      editMode
      onSubmit={updateMask}
      defaultValues={defaultValues}
      uploadUrlData={uploadUrlData}
      refetchUploadCredentials={refetchUploadCredentials}
    >
      {({ isLoading }) => (
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Masks}
          actions={[
            <Button
              key="save-mask"
              type="submit"
              data-testid="save-event-mask"
              isLoading={
                isFetching || isLoading || updateStyleMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      )}
    </MaskForm>
  );
};
